import { Link } from "react-router-dom";

interface Course {
  id: number;
  slug: string;
  icon: string;
  category: string;
  categoryBg: string;
  categoryText: string;
  title: string;
  tagline: string;
  duration: string;
  level: string;
  price: number;
  originalPrice: number;
  students: number;
  rating: number;
  reviews: number;
  nextStart: string;
  format: string;
  image: string;
  instructor: { name: string; role: string; avatar: string; color: string };
}

export default function CourseCard({ course }: { course: Course }) {
  const discount = Math.round(((course.originalPrice - course.price) / course.originalPrice) * 100);

  return (
    <div className="group bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 flex flex-col">
      {/* Image */}
      <div className="relative w-full h-48 overflow-hidden flex-shrink-0">
        <img
          src={course.image}
          alt={course.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent" />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${course.categoryBg} ${course.categoryText}`}>
            {course.category}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-black text-[#C8F135]">
            -{discount}%
          </span>
        </div>
        {/* Level */}
        <div className="absolute bottom-3 left-3">
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-white/90 text-gray-700">
            <i className="ri-bar-chart-2-line mr-1" />{course.level}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        {/* Instructor */}
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white bg-gradient-to-br ${course.instructor.color} flex-shrink-0`}>
            {course.instructor.avatar}
          </div>
          <span className="text-xs text-gray-500 truncate">{course.instructor.name}</span>
        </div>

        <h3 className="text-base font-bold text-gray-900 mb-1 leading-snug">{course.title}</h3>
        <p className="text-xs text-gray-500 mb-3">{course.tagline}</p>

        {/* Meta */}
        <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-4">
          <span className="flex items-center gap-1"><i className="ri-time-line" style={{color: "#9bc400"}} />{course.duration}</span>
          <span className="flex items-center gap-1"><i className="ri-live-line" style={{color: "#9bc400"}} />{course.format}</span>
          <span className="flex items-center gap-1"><i className="ri-group-line" style={{color: "#9bc400"}} />{course.students.toLocaleString()} students</span>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-4">
          <div className="flex gap-0.5">
            {[1,2,3,4,5].map((s) => (
              <i key={s} className={`text-xs ${s <= Math.round(course.rating) ? "ri-star-fill text-[#C8F135]" : "ri-star-line text-gray-300"}`} />
            ))}
          </div>
          <span className="text-xs font-bold text-gray-800">{course.rating}</span>
          <span className="text-xs text-gray-400">({course.reviews})</span>
        </div>

        {/* Price + CTA */}
        <div className="mt-auto flex items-center justify-between">
          <div>
            <span className="text-xl font-black text-gray-900">${course.price}</span>
            <span className="text-sm text-gray-400 line-through ml-2">${course.originalPrice}</span>
          </div>
          <Link
            to={`/academy/${course.slug}`}
            className="px-4 py-2 rounded-full bg-black text-[#C8F135] text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-zinc-800 transition-colors"
          >
            Enroll Now
          </Link>
        </div>
      </div>
    </div>
  );
}
